/**
 * Integration Icon
 *
 * Component for displaying integration icons using React icons
 *
 * Primary responsibilities:
 * - Display appropriate icon for each integration type
 * - Provide consistent styling for integration icons
 */

import { type FC, type ReactNode } from 'react'
import {
  IconBrandAsana,
  IconBrandFacebook,
  IconBrandGithub,
  IconBrandGmail,
  IconBrandGoogleDrive,
  IconBrandGooglePhotos,
  IconBrandInstagram,
  IconBrandNotion,
  IconCalendar,
  IconDatabase,
  IconFileSpreadsheet,
  IconBrandTwitter,
  IconBrandSlack,
  IconMail,
  IconFileText,
  IconBrandYoutube,
  IconCertificate,
  IconListCheck,
  IconBrandReddit,
  IconBrandLinkedin,
  IconCalendarEvent,
  IconVideo,
  IconPuzzle,
  IconServer,
  IconBrandZoom
} from '@tabler/icons-react'
import {
  IconBrandCanva,
  IconBrandHubspot,
  IconBrandJira,
  IconBrandMicrosoft,
  IconCloudStorage,
  IconBrandDropbox
} from '@/components/extensions/custom-icons'

import { cn } from '@/lib/utils'

interface ExtensionIconProps {
  extensionId?: string
  name?: string // toolkit slug like "gmail", "github"
  toolkitLogo?: string
  className?: string
  size?: number
}

const normalizeIconSlug = (slug: string): string => {
  const compactSlug = slug
    .toLowerCase()
    .trim()
    .replace(/[\s_-]+/g, '')
  const slugAliases: Record<string, string> = {
    x: 'twitter',
    meta: 'facebook',
    facebookpages: 'facebook',
    googlemail: 'gmail',
    googleworkspace: 'googledrive',
    msteams: 'microsoftteams',
    microsoftteam: 'microsoftteams',
    microsoftteams: 'microsoftteams',
    microsoftoutlook: 'outlook',
    outlookmail: 'outlook',
    office365: 'outlook',
    onedrive: 'onedrive'
  }

  return slugAliases[compactSlug] || compactSlug
}

const getStableIcon = (slug: string | undefined, size: number): ReactNode | null => {
  if (!slug) {
    return null
  }

  switch (normalizeIconSlug(slug)) {
    case 'gmail':
      return <IconBrandGmail size={size} />
    case 'github':
      return <IconBrandGithub size={size} />
    case 'googlecalendar':
      return <IconCalendar size={size} />
    case 'googlesheets':
      return <IconFileSpreadsheet size={size} />
    case 'twitter':
      return <IconBrandTwitter size={size} />
    case 'notion':
      return <IconBrandNotion size={size} />
    case 'googledrive':
      return <IconBrandGoogleDrive size={size} />
    case 'slack':
      return <IconBrandSlack size={size} />
    case 'outlook':
      return <IconMail size={size} />
    case 'hubspot':
      return <IconBrandHubspot size={size} />
    case 'googledocs':
      return <IconFileText size={size} />
    case 'jira':
      return <IconBrandJira size={size} />
    case 'youtube':
      return <IconBrandYoutube size={size} />
    case 'asana':
      return <IconBrandAsana size={size} />
    case 'googletasks':
    case 'clickup':
      return <IconListCheck size={size} />
    case 'reddit':
      return <IconBrandReddit size={size} />
    case 'linkedin':
      return <IconBrandLinkedin size={size} />
    case 'mcp':
      return <IconServer size={size} />
    case 'skill':
    case 'skills':
      return <IconCertificate size={size} />
    case 'microsoftteams':
      return <IconBrandMicrosoft size={size} />
    case 'wrike':
    case 'calendly':
      return <IconCalendarEvent size={size} />
    case 'googlephotos':
      return <IconBrandGooglePhotos size={size} />
    case 'zoom':
      return <IconBrandZoom size={size} />
    case 'googlemeet':
      return <IconVideo size={size} />
    case 'onedrive':
      return <IconCloudStorage size={size} />
    case 'dropbox':
      return <IconBrandDropbox size={size} />
    case 'supabase':
      return <IconDatabase size={size} />
    case 'facebook':
      return <IconBrandFacebook size={size} />
    case 'instagram':
      return <IconBrandInstagram size={size} />
    case 'canva':
      return <IconBrandCanva size={size} />
    default:
      return null
  }
}

export const ExtensionIcon: FC<ExtensionIconProps> = ({
  extensionId,
  name,
  toolkitLogo,
  className,
  size = 24
}) => {
  const stableIcon = getStableIcon(name, size) || getStableIcon(extensionId, size)
  const fallbackIcon = stableIcon || <IconPuzzle size={size} />

  if (stableIcon) {
    return <div className={cn('flex items-center justify-center', className)}>{fallbackIcon}</div>
  }

  if (toolkitLogo) {
    return (
      <div className={cn('flex items-center justify-center', className)}>
        <img
          src={toolkitLogo}
          alt="Integration logo"
          width={size}
          height={size}
          className="rounded-sm"
          onError={(e) => {
            // Fallback to hardcoded icon if logo fails to load
            const target = e.target as HTMLImageElement
            target.style.display = 'none'
            const fallback = target.nextElementSibling as HTMLElement
            if (fallback) fallback.style.display = 'block'
          }}
        />
        <div style={{ display: 'none' }}>{fallbackIcon}</div>
      </div>
    )
  }

  return <div className={cn('flex items-center justify-center', className)}>{fallbackIcon}</div>
}
